import { useResolutionInfo } from '@mm/hooks';
import { useSubscriptionCRUDActions } from '@mm/state/actions/SubscriptionCRUDActions';
import { isBilling2023Subscription } from '@mm/state/model/SubscriptionModel';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { getCurrentSubscription } from '@mm/state/selectors/subscriptionSelectors';
import React, { useEffect, useState } from 'react';
import { Divider, message } from 'antd';
import { useSelector } from 'react-redux';
import { TABLET_SMALL } from '../../../../../constants/screenSize';
import styles from './VoyagerLeads.module.css';
import classNames from 'classnames';
import { VoyagerFiltersMenu } from './VoyagerFiltersMenu/VoyagerFiltersMenu';
import { VoyagerLeadsSearchQuery } from '@mm/state/interfaces/voyagerLeads';
import { VoyagerLeadsContactsTable } from './VoyagerLeadsContactsTable/VoyagerLeadsContactsTable';
import { VoyagerFiltersBar } from './VoyagerFiltersBar/VoyagerFiltersBar';
import { selectVoyagerLeadsCount } from '@mm/state/selectors/voyagerLeadsSelectors';
import { useVoyagerFilters } from './hooks/use-voyager-filters';

export const VoyagerLeads = () => {
  const [voyagerSearchQuery, setVoyagerSearchQuery] =
    useState<VoyagerLeadsSearchQuery>({
      query: [],
      perPage: 50,
    });
  const { busy, isValueExists, getUpdatedQuery } = useVoyagerFilters({
    fetchAttributes: true,
    voyagerSearchQuery,
  });
  const hasFilters = !!voyagerSearchQuery.query?.length;
  const facebookPage = useSelector(activeFacebookPageSelector);
  const leadsCount = useSelector(selectVoyagerLeadsCount) || 0;

  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= TABLET_SMALL;
  const [messageApi, contextHolder] = message.useMessage();

  const currentSubscription = useSelector(
    getCurrentSubscription(facebookPage?.id),
  );
  const { getSubscription } = useSubscriptionCRUDActions();
  const isNewBillingSubscription =
    facebookPage?.subscription &&
    isBilling2023Subscription(facebookPage?.subscription);

  const setQuery = (params: VoyagerLeadsSearchQuery) => {
    if (isMobile) {
      messageApi.success(
        'Filters have been applied. Scroll down to see the results.',
      );
    }
    return setVoyagerSearchQuery({ ...params });
  };

  useEffect(() => {
    if (
      !currentSubscription &&
      facebookPage?.subscription &&
      isNewBillingSubscription
    ) {
      getSubscription(facebookPage?.subscription.id, facebookPage?.id);
    }
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        {!isMobile && (
          <div>
            B2B Leads
            <div className={styles.textSmall}>Select filters to find leads</div>
          </div>
        )}
      </div>
      {contextHolder}

      <div
        className={classNames(styles.content, {
          [styles.contentEmptyState]: !leadsCount,
        })}
      >
        <VoyagerFiltersMenu
          voyagerSearchQuery={voyagerSearchQuery}
          setVoyagerSearchQuery={setQuery}
          getUpdatedQuery={getUpdatedQuery}
          isValueExists={isValueExists}
          busy={busy}
        />
        <div className={styles.contacts}>
          <VoyagerFiltersBar
            voyagerSearchQuery={voyagerSearchQuery}
            setVoyagerSearchQuery={setQuery}
            getUpdatedQuery={getUpdatedQuery}
          />

          {hasFilters && <Divider style={{ margin: '12px 0px 22px' }} />}

          <VoyagerLeadsContactsTable
            voyagerSearchQuery={voyagerSearchQuery}
            setVoyagerSearchQuery={setQuery}
          />
        </div>
      </div>
    </div>
  );
};
